import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress,
  Collapse, Divider, Grid, IconButton, InputAdornment,
  List, ListItem, ListItemText, MenuItem, Snackbar, Stack,
  Switch, Tab, Tabs, TextField, Tooltip, Typography,
} from '@mui/material';
import {
  ArrowBack, ExpandLess, ExpandMore, InfoOutlined,
  Lock, RestartAlt, Save, Tune,
} from '@mui/icons-material';
import SearchIcon from '@mui/icons-material/Search';
import { adminApi } from '../../api/adminApi';

// ── Addon-to-feature defaults (mirrors FeatureLimitsPage) ────────────────────
const DEFAULT_ADDON_FEATURES = {
  'inventory-items': [
    'inventory.batch_operations', 'inventory.low_stock_alerts',
    'inventory.fifo_lifo_costing', 'inventory.reorder_point_automation',
    'inventory.negative_stock_allow', 'inventory.inventory_reservations',
    'inventory.unit_of_measure_conversion',
    'inventory.inventory_forecasting', 'inventory.cycle_count',
  ],
  'inventory-bundles': [
    'inventory.bundle_products',
    'inventory.bundle_assembly',
  ],
  'lot-tracking': [
    'inventory.lot_number_tracking', 'inventory.expiry_date_tracking',
    'inventory.serial_number_tracking',
  ],
  'barcode-scanning': [
    'inventory.barcode_scanning', 'integrations.barcode_label_printing',
  ],
  'warehouses': [
    'warehousing.multi_warehouse', 'warehousing.bin_location_management',
    'warehousing.transfer_orders', 'warehousing.receiving_workflows',
    'warehousing.putaway_rules', 'warehousing.wave_picking',
    'warehousing.cross_docking', 'warehousing.zone_management',
  ],
  'history-retention': ['reporting.audit_log_export'],
  'scheduled-reports': [
    'reporting.scheduled_reports', 'reporting.custom_dashboards',
    'reporting.advanced_analytics', 'reporting.inventory_valuation_reports',
    'reporting.cost_analysis',
  ],
  'data-exports': [
    'reporting.export_csv', 'reporting.export_excel', 'reporting.export_pdf',
  ],
  'notifications': [
    'notifications.email_alerts', 'notifications.sms_alerts',
    'notifications.slack_integration', 'notifications.in_app_notifications',
    'notifications.push_notifications', 'notifications.digest_emails',
  ],
  'advanced-audit-trail': [
    'security.advanced_audit_log', 'security.data_masking',
    'security.field_level_encryption',
  ],
  'team-users': [
    'security.mfa_enforcement', 'security.ip_allowlisting',
    'security.sso_saml', 'security.session_timeout_policy',
    'licensing.multi_user_access',
  ],
  'api-requests': [
    'api_management.api_key_self_service', 'api_management.rate_limit_override',
    'api_management.graphql_api', 'api_management.webhook_delivery',
    'api_management.api_usage_analytics', 'integrations.webhooks',
    'integrations.public_api_access',
  ],
};

const DEFAULT_BASE_PLAN_FEATURES = [
  'integrations.ecommerce_connectors', 'integrations.erp_sync',
  'integrations.edi_support', 'integrations.accounting_integration',
  'integrations.shipping_carriers', 'licensing.usage_dashboard',
  'licensing.quota_warnings', 'licensing.self_service_billing',
  'licensing.custom_branding',
];

// Categorized features for the Basic Inventory card (mirrors FeatureLimitsPage)
const DEFAULT_INCLUDED_FEATURES = [
  { category: 'Inventory', features: [
    { key: 'inventory.batch_operations', label: 'Batch Operations' },
    { key: 'inventory.low_stock_alerts', label: 'Low Stock Alerts' },
  ]},
  { category: 'Warehousing', features: [
    { key: 'warehousing.receiving_workflows', label: 'Receiving Workflows' },
  ]},
  { category: 'Reporting', features: [
    { key: 'reporting.export_csv', label: 'CSV Export' },
  ]},
  { category: 'Integrations', features: [
    { key: 'integrations.public_api_access', label: 'Public API Access' },
  ]},
  { category: 'Security', features: [
    { key: 'security.session_timeout_policy', label: 'Session Timeout Policy' },
  ]},
  { category: 'Notifications', features: [
    { key: 'notifications.email_alerts', label: 'Email Alerts' },
    { key: 'notifications.in_app_notifications', label: 'In-App Notifications' },
  ]},
  { category: 'API Management', features: [
    { key: 'api_management.api_key_self_service', label: 'Self-Service API Keys' },
    { key: 'api_management.api_usage_analytics', label: 'API Usage Analytics' },
  ]},
  { category: 'Licensing', features: [
    { key: 'licensing.usage_dashboard', label: 'Usage Dashboard' },
    { key: 'licensing.quota_warnings', label: 'Quota Warning Notifications' },
    { key: 'licensing.multi_user_access', label: 'Multi-User Access' },
  ]},
];

const DEFAULT_ADDON_CATEGORIES = {
  'base-plan':            'Platform',
  'inventory-items':      'Inventory',
  'lot-tracking':         'Inventory',
  'barcode-scanning':     'Inventory',
  'inventory-bundles':    'Inventory',
  'warehouses':           'Warehousing',
  'history-retention':    'Reporting',
  'scheduled-reports':    'Reporting',
  'data-exports':         'Reporting',
  'notifications':        'Notifications',
  'advanced-audit-trail': 'Security',
  'team-users':           'Security',
  'api-requests':         'API & Integrations',
};

const ADDON_LABELS = {
  'base-plan':            'Basic Inventory',
  'inventory-items':      'Inventory Items',
  'lot-tracking':         'Lot & Batch Tracking',
  'barcode-scanning':     'Barcode Scanning',
  'inventory-bundles':    'Inventory Bundles',
  'warehouses':           'Warehouses / Locations',
  'history-retention':    'History Retention',
  'scheduled-reports':    'Scheduled Reports',
  'data-exports':         'Data Exports',
  'notifications':        'Real-Time Notifications',
  'advanced-audit-trail': 'Advanced Audit Trail',
  'team-users':           'Team Members',
  'api-requests':         'API Requests',
};

const CATEGORY_ORDER = ['All', 'Inventory', 'Warehousing', 'Reporting', 'Notifications', 'Security', 'API & Integrations', 'Platform'];
const LEVEL_ORDER = ['default', 'standard', 'professional', 'custom'];
const LEVEL_COLORS = { default: 'default', standard: 'primary', professional: 'secondary', custom: 'warning' };
const PLAN_COLOR = { trial: 'default', paid: 'primary' };

// ── Build effective feature-to-addon mapping ─────────────────────────────────
function buildEffectiveMapping(featureMapping) {
  const mapping = {};
  for (const [addonId, featureKeys] of Object.entries(DEFAULT_ADDON_FEATURES)) {
    for (const key of featureKeys) { mapping[key] = addonId; }
  }
  for (const key of DEFAULT_BASE_PLAN_FEATURES) { mapping[key] = 'base-plan'; }
  for (const [featureKey, addonId] of Object.entries(featureMapping || {})) {
    mapping[featureKey] = addonId;
  }
  return mapping;
}

// ── Feature metadata lookup from grouped response ────────────────────────────
function buildFeatureList(grouped) {
  const list = [];
  for (const items of Object.values(grouped)) {
    for (const f of items) { list.push(f); }
  }
  return list;
}

// ── Addon Card ───────────────────────────────────────────────────────────────
function TenantAddonCard({
  addon, isBasePlan, features, rawFeatures, pending, onToggle,
  platformDisabledAddons, platformDisabledFeatures,
  currentLevel, onLevelChange, search, basePlanFeatureGroups,
}) {
  const [expanded, setExpanded] = useState(false);

  const isAddonPlatformDisabled = platformDisabledAddons.includes(addon.id);

  // Filter features by search
  const q = (search || '').toLowerCase();
  const visibleFeatures = features.filter((f) =>
    !q || f.label.toLowerCase().includes(q) || f.key.toLowerCase().includes(q)
  );

  // Count overrides in this card
  const overrideCount = visibleFeatures.filter((f) => f.key in pending).length;

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: 3,
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        borderColor: isBasePlan ? 'success.main' : isAddonPlatformDisabled ? 'error.light' : 'divider',
        borderWidth: isBasePlan ? 2 : 1,
        opacity: isAddonPlatformDisabled ? 0.6 : 1,
      }}
    >
      <CardContent sx={{ flex: 1, pb: 1 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start" mb={1}>
          <Box flex={1}>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="subtitle1" fontWeight={700}>{addon.label}</Typography>
              {isAddonPlatformDisabled && (
                <Chip label="Platform Disabled" size="small" color="error" sx={{ fontSize: '0.6rem', height: 18 }} />
              )}
              {overrideCount > 0 && (
                <Chip label={`${overrideCount} modified`} size="small" color="warning" sx={{ fontSize: '0.6rem', height: 18 }} />
              )}
            </Stack>
            {addon.description && (
              <Typography variant="caption" color="text.secondary" display="block">
                {addon.description}
              </Typography>
            )}
          </Box>
          <Chip
            label={addon.category || 'Platform'}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.65rem', height: 20 }}
          />
        </Stack>

        {/* Level selector for built-in metered add-ons */}
        {!isBasePlan && addon.isBuiltIn && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Level
            </Typography>
            <TextField
              select
              size="small"
              fullWidth
              value={currentLevel || 'default'}
              onChange={(e) => onLevelChange(addon.id, e.target.value)}
              sx={{ mt: 0.5 }}
              SelectProps={{ native: false }}
              InputLabelProps={{ shrink: true }}
            >
              {LEVEL_ORDER.map((lvl) => (
                <MenuItem key={lvl} value={lvl}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      label={lvl === 'default' ? 'Basic' : lvl.charAt(0).toUpperCase() + lvl.slice(1)}
                      size="small"
                      color={LEVEL_COLORS[lvl] !== 'default' ? LEVEL_COLORS[lvl] : 'default'}
                      sx={{ fontWeight: 600, fontSize: '0.65rem', height: 20 }}
                    />
                  </Stack>
                </MenuItem>
              ))}
            </TextField>
          </Box>
        )}
      </CardContent>

      {/* Categorized features — base plan card */}
      {isBasePlan && basePlanFeatureGroups && basePlanFeatureGroups.length > 0 && (
        <>
          <Divider />
          <Box
            sx={{ px: 2, py: 0.5, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            onClick={() => setExpanded((e) => !e)}
          >
            <Typography variant="caption" fontWeight={600} color="text.secondary">
              {basePlanFeatureGroups.reduce((sum, g) => sum + g.features.length, 0)} features in base plan
            </Typography>
            {expanded ? <ExpandLess sx={{ fontSize: 18, color: 'text.secondary' }} /> : <ExpandMore sx={{ fontSize: 18, color: 'text.secondary' }} />}
          </Box>
          <Collapse in={expanded}>
            <List dense disablePadding sx={{ px: 1, pb: 1, mt: 0.5 }}>
              {basePlanFeatureGroups.map(({ category, features: catFeatures }) => {
                const filtered = catFeatures.filter((f) =>
                  !q || f.label.toLowerCase().includes(q) || f.key.toLowerCase().includes(q)
                );
                if (filtered.length === 0) { return null; }
                return (
                  <Box key={category} sx={{ mb: 0.5 }}>
                    <Typography variant="caption" fontWeight={700} display="block" sx={{ mb: 0.25, px: 1 }}>
                      {category}
                    </Typography>
                    {filtered.map(({ key, label }) => {
                      const isEnabled = rawFeatures[key] ?? false;
                      const isDirty = key in pending;
                      const isPlatformDisabled = platformDisabledFeatures.includes(key);
                      return (
                        <ListItem
                          key={key}
                          disableGutters
                          sx={{
                            py: 0.25, px: 1,
                            bgcolor: isDirty ? 'warning.50' : 'transparent',
                            borderRadius: 1,
                            opacity: isPlatformDisabled ? 0.55 : 1,
                          }}
                        >
                          <Tooltip title={isPlatformDisabled ? 'Disabled at the platform level — cannot be changed here. Re-enable it in Feature Limits & Add-Ons.' : ''}>
                            <span style={{ display: 'inline-flex' }}>
                              <Switch
                                size="small"
                                checked={isPlatformDisabled ? false : isEnabled}
                                disabled={isPlatformDisabled}
                                onChange={() => onToggle(key, isEnabled)}
                                color="success"
                                sx={{ mr: 0.5 }}
                              />
                            </span>
                          </Tooltip>
                          <ListItemText
                            primary={
                              <Stack direction="row" spacing={0.5} alignItems="center">
                                <Typography variant="caption" fontSize="0.72rem">{label}</Typography>
                                {isPlatformDisabled && (
                                  <Chip icon={<Lock sx={{ fontSize: '11px !important' }} />} label="Platform Off" size="small" color="error" variant="outlined" sx={{ fontSize: '0.55rem', height: 16 }} />
                                )}
                                {!isPlatformDisabled && !isEnabled && (
                                  <Chip label="Tenant Off" size="small" variant="outlined" sx={{ fontSize: '0.55rem', height: 16 }} />
                                )}
                                {isDirty && (
                                  <Chip label="modified" size="small" color="warning" sx={{ fontSize: '0.55rem', height: 16 }} />
                                )}
                              </Stack>
                            }
                          />
                        </ListItem>
                      );
                    })}
                  </Box>
                );
              })}
            </List>
          </Collapse>
        </>
      )}

      {/* Features list — collapsible (non-base-plan cards) */}
      {!isBasePlan && visibleFeatures.length > 0 && (
        <>
          <Divider />
          <Box
            sx={{ px: 2, py: 0.5, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
            onClick={() => setExpanded((e) => !e)}
          >
            <Typography variant="caption" fontWeight={600} color="text.secondary">
              {visibleFeatures.length} feature{visibleFeatures.length !== 1 ? 's' : ''}
            </Typography>
            {expanded ? <ExpandLess sx={{ fontSize: 18, color: 'text.secondary' }} /> : <ExpandMore sx={{ fontSize: 18, color: 'text.secondary' }} />}
          </Box>
          <Collapse in={expanded}>
            <List dense disablePadding sx={{ px: 1, pb: 1 }}>
              {visibleFeatures.map((f) => {
                const isEnabled = rawFeatures[f.key] ?? false;
                const isDirty = f.key in pending;
                const isPlatformDisabled = platformDisabledFeatures.includes(f.key);

                return (
                  <ListItem
                    key={f.key}
                    disableGutters
                    sx={{
                      py: 0.25, px: 1,
                      bgcolor: isDirty ? 'warning.50' : 'transparent',
                      borderRadius: 1,
                      opacity: isPlatformDisabled ? 0.55 : 1,
                    }}
                  >
                    <Tooltip title={isPlatformDisabled ? 'Disabled at the platform level — cannot be changed here. Re-enable it in Feature Limits & Add-Ons.' : ''}>
                      <span style={{ display: 'inline-flex' }}>
                        <Switch
                          size="small"
                          checked={isPlatformDisabled ? false : isEnabled}
                          disabled={isPlatformDisabled}
                          onChange={() => onToggle(f.key, isEnabled)}
                          color="success"
                          sx={{ mr: 0.5 }}
                        />
                      </span>
                    </Tooltip>
                    <ListItemText
                      primary={
                        <Stack direction="row" spacing={0.5} alignItems="center">
                          <Typography variant="caption" fontSize="0.72rem">{f.label}</Typography>
                          {isPlatformDisabled && (
                            <Chip icon={<Lock sx={{ fontSize: '11px !important' }} />} label="Platform Off" size="small" color="error" variant="outlined" sx={{ fontSize: '0.55rem', height: 16 }} />
                          )}
                          {!isPlatformDisabled && !isEnabled && (
                            <Chip label="Tenant Off" size="small" variant="outlined" sx={{ fontSize: '0.55rem', height: 16 }} />
                          )}
                          {isDirty && (
                            <Chip label="modified" size="small" color="warning" sx={{ fontSize: '0.55rem', height: 16 }} />
                          )}
                        </Stack>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          </Collapse>
        </>
      )}
    </Card>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function TenantFeaturesPage() {
  const { tenantId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const [tenant, setTenant] = useState(null);
  const [rawFeatures, setRawFeatures] = useState({});
  const [grouped, setGrouped] = useState({});
  const [featureLevels, setFeatureLevels] = useState({});
  const [platformConfig, setPlatformConfig] = useState({});
  const [pending, setPending] = useState({});
  const [pendingLevels, setPendingLevels] = useState({});
  const [search, setSearch] = useState('');
  const [categoryTab, setCategoryTab] = useState('All');

  // ─── Load ─────────────────────────────────────────────────────────────────
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await adminApi.getTenantFeatures(tenantId);
      setTenant({ name: data.tenantName, plan: data.plan });
      setRawFeatures(data.raw);
      setGrouped(data.features);
      setFeatureLevels(data.featureLevels || {});
      setPlatformConfig(data.platformConfig || {});
      setPending({});
      setPendingLevels({});
    } catch (e) {
      setError(e.message || 'Failed to load feature flags.');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  // ─── Toggle a feature flag ────────────────────────────────────────────────
  const handleToggle = (key, currentValue) => {
    const newValue = !currentValue;
    setPending((prev) => ({ ...prev, [key]: newValue }));
    setRawFeatures((prev) => ({ ...prev, [key]: newValue }));
  };

  // ─── Change feature level ─────────────────────────────────────────────────
  const handleLevelChange = (featureId, level) => {
    setPendingLevels((prev) => ({ ...prev, [featureId]: level }));
    setFeatureLevels((prev) => ({ ...prev, [featureId]: level }));
  };

  // ─── Save ─────────────────────────────────────────────────────────────────
  const handleSave = async () => {
    const hasFeatChanges = Object.keys(pending).length > 0;
    const hasLevelChanges = Object.keys(pendingLevels).length > 0;
    if (!hasFeatChanges && !hasLevelChanges) {
      return;
    }

    setSaving(true);
    try {
      const payload = {};
      if (hasFeatChanges) {
        payload.features = pending;
      }
      if (hasLevelChanges) {
        payload.featureLevels = pendingLevels;
      }
      await adminApi.bulkUpdateTenantFeatures(tenantId, payload);
      setPending({});
      setPendingLevels({});
      const count = Object.keys(pending).length + Object.keys(pendingLevels).length;
      setToast({ open: true, message: `Saved ${count} change(s).`, severity: 'success' });
    } catch (e) {
      setToast({ open: true, message: `Save failed: ${e.message}`, severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // ─── Reset ────────────────────────────────────────────────────────────────
  const handleReset = async () => {
    if (!window.confirm(`Reset all feature flags for "${tenant?.name}" to defaults?`)) {
      return;
    }
    setResetting(true);
    try {
      await adminApi.resetTenantFeatures(tenantId);
      setToast({ open: true, message: 'Feature flags reset to defaults.', severity: 'info' });
      await load();
    } catch (e) {
      setToast({ open: true, message: `Reset failed: ${e.message}`, severity: 'error' });
    } finally {
      setResetting(false);
    }
  };

  // ─── Build addon cards ────────────────────────────────────────────────────
  const featureMapping = platformConfig.featureMapping || {};
  const customAddons = platformConfig.customAddons || [];
  const platformDisabledAddons = platformConfig.disabledAddons || [];
  const platformDisabledFeatures = platformConfig.disabledFeatures || [];
  const basePlanFeaturesKeys = platformConfig.basePlanFeatures || [];

  const effectiveMapping = buildEffectiveMapping(featureMapping);
  // Map any unmapped features to base-plan
  const allFeatures = buildFeatureList(grouped);
  for (const f of allFeatures) {
    if (!effectiveMapping[f.key]) {
      effectiveMapping[f.key] = 'base-plan';
    }
  }

  // Build base plan feature groups from basePlanFeatures config (or fall back to DEFAULT_INCLUDED_FEATURES)
  const basePlanFeatureGroups = (() => {
    if (basePlanFeaturesKeys.length > 0) {
      const featureMap = {};
      for (const f of allFeatures) { featureMap[f.key] = f; }
      const groups = {};
      for (const key of basePlanFeaturesKeys) {
        const feat = featureMap[key];
        if (!feat) { continue; }
        const cat = feat.category || 'Other';
        if (!groups[cat]) { groups[cat] = []; }
        groups[cat].push({ key: feat.key, label: feat.label });
      }
      return Object.entries(groups).map(([category, features]) => ({ category, features }));
    }
    return DEFAULT_INCLUDED_FEATURES;
  })();

  // Build addon card list
  const builtInAddonIds = Object.keys(DEFAULT_ADDON_FEATURES);
  const allAddons = [
    { id: 'base-plan', label: 'Basic Inventory', description: 'Platform essentials included with every plan.', category: 'Platform', isBasePlan: true, isBuiltIn: false },
    ...builtInAddonIds.map((id) => ({
      id,
      label: ADDON_LABELS[id] || id,
      description: '',
      category: DEFAULT_ADDON_CATEGORIES[id] || 'Platform',
      isBasePlan: false,
      isBuiltIn: true,
    })),
    ...customAddons.map((a) => ({
      ...a,
      category: a.category || 'Platform',
      isBasePlan: false,
      isBuiltIn: false,
    })),
  ];

  // Filter by category, then order: Basic Inventory always first, then
  // alphabetical by name, with platform-disabled cards sorted to the bottom.
  const filteredAddons = allAddons
    .filter((a) => {
      if (categoryTab === 'All') {
        return true;
      }
      return a.category === categoryTab;
    })
    .sort((a, b) => {
      if (Boolean(a.isBasePlan) !== Boolean(b.isBasePlan)) { return a.isBasePlan ? -1 : 1; }
      const aDis = platformDisabledAddons.includes(a.id);
      const bDis = platformDisabledAddons.includes(b.id);
      if (aDis !== bDis) { return aDis ? 1 : -1; }
      return (a.label || '').localeCompare(b.label || '');
    });

  // Get features for an addon
  const getFeaturesForAddon = (addonId) =>
    allFeatures.filter((f) => effectiveMapping[f.key] === addonId);

  const pendingCount = Object.keys(pending).length + Object.keys(pendingLevels).length;

  // ─── Render ───────────────────────────────────────────────────────────────
  if (loading) {
    return <Box display="flex" justifyContent="center" mt={8}><CircularProgress /></Box>;
  }

  if (error) {
    return <Alert severity="error" sx={{ m: 3 }}>{error}</Alert>;
  }

  return (
    <Box maxWidth={1400} mx="auto" px={3} py={4}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="flex-end" mb={1}>
        <Box>
          <Stack direction="row" spacing={1.5} alignItems="center">
            <IconButton onClick={() => navigate(-1)} size="small">
              <ArrowBack />
            </IconButton>
            <Tune sx={{ color: 'primary.main', fontSize: 28 }} />
            <Typography variant="h5" fontWeight={700}>Tenant Feature Flags</Typography>
          </Stack>
          <Stack direction="row" spacing={1} alignItems="center" mt={0.5} ml={6}>
            <Typography variant="body2" color="text.secondary">{tenant?.name}</Typography>
            <Chip
              label={tenant?.plan?.toUpperCase()}
              color={PLAN_COLOR[tenant?.plan] || 'default'}
              size="small"
            />
            {pendingCount > 0 && (
              <Chip label={`${pendingCount} unsaved`} color="warning" size="small" />
            )}
          </Stack>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button
            variant="outlined"
            color="warning"
            startIcon={<RestartAlt />}
            onClick={handleReset}
            disabled={resetting || saving}
          >
            {resetting ? 'Resetting...' : 'Reset Defaults'}
          </Button>
          <Button variant="outlined" onClick={load} disabled={loading || saving}>
            Reload
          </Button>
          <Button
            variant="contained"
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <Save />}
            disabled={pendingCount === 0 || saving}
            onClick={handleSave}
          >
            {`Save${pendingCount > 0 ? ` (${pendingCount})` : ''}`}
          </Button>
        </Stack>
      </Stack>

      {/* Search */}
      <TextField
        fullWidth
        size="small"
        placeholder="Search features..."
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 2, mt: 1 }}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
        }}
      />

      {/* Category tabs */}
      <Box sx={{ mb: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={categoryTab}
          onChange={(_, v) => setCategoryTab(v)}
          variant="scrollable"
          scrollButtons="auto"
          sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 600, fontSize: '0.85rem' } }}
        >
          {CATEGORY_ORDER.map((cat) => (
            <Tab key={cat} label={cat} value={cat} />
          ))}
        </Tabs>
      </Box>

      {(platformDisabledAddons.length > 0 || platformDisabledFeatures.length > 0) && (
        <Alert severity="info" icon={<InfoOutlined />} sx={{ mb: 3, borderRadius: 2 }}>
          Features disabled at the platform level are locked here and cannot be overridden for a tenant.
          To change them, re-enable the feature in Feature Limits &amp; Add-Ons.
        </Alert>
      )}

      <Grid container spacing={3}>
        {filteredAddons.map((addon) => {
          const addonFeatures = getFeaturesForAddon(addon.id);
          return (
            <Grid item xs={12} sm={6} md={4} key={addon.id}>
              <TenantAddonCard
                addon={addon}
                isBasePlan={addon.isBasePlan}
                features={addonFeatures}
                rawFeatures={rawFeatures}
                pending={pending}
                onToggle={handleToggle}
                platformDisabledAddons={platformDisabledAddons}
                platformDisabledFeatures={platformDisabledFeatures}
                currentLevel={featureLevels[addon.id] || 'default'}
                onLevelChange={handleLevelChange}
                search={search}
                basePlanFeatureGroups={basePlanFeatureGroups}
              />
            </Grid>
          );
        })}
      </Grid>

      <Typography variant="caption" color="text.secondary" display="block" mt={2}>
        Feature flag changes take effect on the tenant&apos;s next API call. Level changes apply immediately.
      </Typography>

      {/* Toast */}
      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast((t) => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={toast.severity} onClose={() => setToast((t) => ({ ...t, open: false }))}>
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
